"use client";

import { useEffect, useRef } from "react";

const riskTable = [
    {
        risk: "Commodity Price Risk",
        description: "Exposure to LME Antimony price movements",
        seller: "Shared (floating price)",
        buyer: "Shared (floating price)",
        mitigation: "Fixed-price option available ≤12M",
    },
    {
        risk: "Supply Interruption",
        description: "Failure to deliver contracted volume",
        seller: "Full liability",
        buyer: "None",
        mitigation: "Bonded buffer stock at Rotterdam / Antwerp",
    },
    {
        risk: "Quality Non-Conformance",
        description: "Sb grade below contracted specification",
        seller: "Full liability + replacement",
        buyer: "None",
        mitigation: "Joint PSI + umpire assay protocol",
    },
    {
        risk: "Transit & Insurance",
        description: "Loss or damage in transit (CIF basis)",
        seller: "Full (CIF terms)",
        buyer: "None (CIF terms)",
        mitigation: "ICC (A) all-risks marine insurance",
    },
    {
        risk: "Regulatory / Export",
        description: "Export licence denial or sanction exposure",
        seller: "Full liability",
        buyer: "None",
        mitigation: "Pre-shipment export licence obtained",
    },
    {
        risk: "Payment Default",
        description: "Buyer fails to pay on due date",
        seller: "None",
        buyer: "Full liability",
        mitigation: "L/C at sight or credit insurance",
    },
    {
        risk: "Force Majeure",
        description: "Event beyond reasonable control",
        seller: "Shared",
        buyer: "Shared",
        mitigation: "60-day trigger; make-whole mechanism",
    },
    {
        risk: "Change of Control",
        description: "Material change in ownership of either party",
        seller: "Consent required",
        buyer: "Consent required",
        mitigation: "Change of control clause; 30-day notice",
    },
];

function RiskBadge({ text }: { text: string }) {
    const isNone = text === "None";
    const isShared = text.toLowerCase().startsWith("shared");
    const isFull = text.toLowerCase().startsWith("full");

    const style: React.CSSProperties = {
        fontFamily: "var(--font-sans)",
        fontSize: "0.75rem",
        fontWeight: 500,
        padding: "3px 10px",
        borderRadius: "2px",
        whiteSpace: "nowrap",
        background: isNone
            ? "rgba(74,198,116,0.08)"
            : isShared
                ? "rgba(198,161,74,0.1)"
                : isFull
                    ? "rgba(224,82,82,0.08)"
                    : "rgba(138,155,176,0.1)",
        color: isNone
            ? "#4AC674"
            : isShared
                ? "#e5e7eb"
                : isFull
                    ? "#E05252" : "var(--color-muted)",
        border: isNone
            ? "1px solid rgba(74,198,116,0.2)"
            : isShared
                ? "1px solid rgba(198,161,74,0.2)"
                : isFull
                    ? "1px solid rgba(224,82,82,0.15)"
                    : "1px solid rgba(138,155,176,0.2)",
    };

    return <span style={style}>{text}</span>;
}

export default function RiskAllocationSection() {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const els = ref.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.08 }
        );
        els.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ padding: "120px 0" }}
            aria-labelledby="risk-allocation"
            id="risk-allocation"
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
            <div
                className="px-6 lg:px-12 text-center"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                <div
                    className="flex items-center justify-center gap-3 mb-8 reveal"
                >
                    <span className="type-subheading">Risk Allocation</span>
                </div>
                <h1
                    className="reveal reveal-delay-1"
                    style={{
                        fontFamily: "var(--font-serif)",
                        fontSize: "clamp(2.5rem, 5vw, 4rem)",
                        fontWeight: 400,
                        color: "var(--color-white)",
                        lineHeight: 1.05,
                        letterSpacing: "-0.02em",
                        margin: "0 auto",
                    }}
                >
                        Clear risk allocation for both counterparties.
                </h1>
                <p
                    className="reveal reveal-delay-2"
                    style={{
                        fontFamily: "var(--font-sans)",
                        fontSize: "1.0625rem",
                        color: "var(--color-white-muted)",
                        lineHeight: "1.7",
                        marginTop: "20px",
                        margin: "20px auto 0",
                    }}
                >
                        We believe counterparties should enter agreements with full
                        visibility on who bears which risk. The table below reflects
                        our standard allocation; deviations are negotiable.
                </p>
            </div>

                {/* Legend */}
                <div className="reveal flex flex-wrap gap-4 mb-8 mt-10">
                    {[
                        { label: "None / Protected", color: "#4AC674", bg: "rgba(74,198,116,0.08)", border: "rgba(74,198,116,0.2)" },
                        { label: "Shared", color: "#C6A14A", bg: "rgba(198,161,74,0.1)", border: "rgba(198,161,74,0.2)" },
                        { label: "Full Liability", color: "#E05252", bg: "rgba(224,82,82,0.08)", border: "rgba(224,82,82,0.15)" },
                    ].map((l) => (
                        <div key={l.label} className="flex items-center gap-2">
                            <span
                                style={{
                                    width: "10px",
                                    height: "10px",
                                    borderRadius: "2px",
                                    background: l.bg,
                                    border: `1px solid ${l.border}`,
                                    flexShrink: 0,
                                }}
                                aria-hidden="true"
                            />
                            <span
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "0.75rem",
                                    color: "--color-white",
                                }}
                            >
                                {l.label}
                            </span>
                        </div>
                    ))}
                </div>

                {/* Table */}
                <div
                    className="reveal reveal-delay-1"
                    style={{ overflowX: "auto" }}
                >
                    <table
                        className="data-table"
                        aria-label="Risk allocation between seller and buyer"
                    >
                        <thead>
                            <tr>
                                {["Risk", "Description", "Seller", "Buyer", "Mitigation"].map((h) => (
                                    <th key={h}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {riskTable.map((row) => (
                                <tr key={row.risk}>
                                    <td style={{ fontWeight: 600, color: "var(--color-white)", fontSize: "0.875rem" }}>
                                        {row.risk}
                                    </td>
                                    <td style={{ color: "rgba(255, 255, 255, 0.75)", fontSize: "0.8125rem" }}>
                                        {row.description}
                                    </td>
                                    <td>
                                        <RiskBadge text={row.seller} />
                                    </td>
                                    <td>
                                        <RiskBadge text={row.buyer} />
                                    </td>
                                    <td style={{ color: "rgba(255, 255, 255, 0.75)", fontSize: "0.8125rem" }}>
                                        {row.mitigation}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}